import { useId, useLayoutEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Temporal } from '@js-temporal/polyfill'
import {
  ArrowLeftIcon,
  Button,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  DateTimePicker,
  DropdownMenu,
  Flex,
  Heading,
  Label,
  Separator,
} from '@buffer-mono/popcorn'

import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import {
  getGuestHourCycle,
  getGuestWeekStartsOn,
} from '~publish/helpers/guestTimeFormat'
import { formatTimestamp } from '~publish/helpers/temporal'
import { ScheduleOptionItem } from '~publish/legacy/composer/composer/components/composer-footer/schedule-selector/ScheduleOptionItem'

import styles from './PublicComposerPage.module.css'

export function PublicPostingTimeSelector({
  value,
  disabled,
  onChange,
}: {
  value: number | undefined
  disabled: boolean
  onChange: (value: number | undefined) => void
}): JSX.Element {
  const [view, setView] = useState<'closed' | 'options' | 'date'>('closed')
  const [dateTime, setDateTime] = useState<Temporal.ZonedDateTime>()
  const dateHeadingRef = useRef<HTMLHeadingElement>(null)
  const timeInputId = useId()
  const now = Temporal.Now.zonedDateTimeISO(CURRENT_TIME_ZONE)
  const hourCycle = getGuestHourCycle()
  const label = value
    ? formatTimestamp(value / 1000, {
        timezone: CURRENT_TIME_ZONE,
        twentyFourHourTimeFormat: hourCycle === 24,
      })
    : 'Now'

  useLayoutEffect(() => {
    if (view === 'date') dateHeadingRef.current?.focus()
  }, [view])

  const selectTime = (nextValue: number | undefined): void => {
    onChange(nextValue)
    setView('closed')
  }

  const changeDateTime = (nextDateTime?: Temporal.ZonedDateTime): void => {
    setDateTime(nextDateTime)
    if (nextDateTime && nextDateTime.epochMilliseconds > Date.now()) {
      onChange(nextDateTime.epochMilliseconds)
    }
  }

  const openDatePicker = (event: Event): void => {
    event.preventDefault()
    setDateTime(
      value
        ? Temporal.Instant.fromEpochMilliseconds(value).toZonedDateTimeISO(
            CURRENT_TIME_ZONE,
          )
        : now.add({ hours: 1 }).round({ smallestUnit: 'minute' }),
    )
    setView('date')
  }

  return (
    <DropdownMenu
      open={view !== 'closed'}
      onOpenChange={(open): void => setView(open ? 'options' : 'closed')}
    >
      <DropdownMenu.Trigger>
        <Button
          variant="secondary"
          size="large"
          disabled={disabled}
          className={styles.postingTimeTrigger}
          aria-label={`Posting time: ${label}`}
        >
          {value ? <CalendarIcon /> : <ClockIcon />}
          <span className={styles.postingTimeLabel}>{label}</span>
          <ChevronDownIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        side="top"
        align="end"
        aria-label="Posting time"
        className={
          view === 'date' ? styles.postingTimePopover : styles.postingTimeMenu
        }
      >
        {view === 'date' ? (
          <Flex
            direction="column"
            gap="sm"
            onKeyDown={(event): void => {
              if (event.key !== 'Escape') event.stopPropagation()
            }}
          >
            <Heading ref={dateHeadingRef} tabIndex={-1} size="sm">
              Set Date and Time
            </Heading>
            <DateTimePicker
              value={dateTime}
              onValueChange={changeDateTime}
              timezone={CURRENT_TIME_ZONE}
              min={now}
              weekStartsOn={getGuestWeekStartsOn()}
              hourCycle={hourCycle}
              required
              className={styles.postingDatePicker}
            >
              <DateTimePicker.Calendar />
              <DateTimePicker.Separator />
              <Label htmlFor={timeInputId} size="small">
                Select Time
              </Label>
              <DateTimePicker.TimeInput id={timeInputId} />
            </DateTimePicker>
            <Separator className={styles.postingDateSeparator} />
            <Flex justify="between" align="center" gap="sm" fullWidth>
              <Button
                variant="tertiary"
                onClick={(): void => setView('options')}
              >
                <ArrowLeftIcon /> Back
              </Button>
              <Button
                variant="tertiary"
                disabled={
                  !dateTime ||
                  dateTime.epochMilliseconds <= now.epochMilliseconds
                }
                onClick={(): void => {
                  if (dateTime && dateTime.epochMilliseconds > Date.now()) {
                    selectTime(dateTime.epochMilliseconds)
                  }
                }}
              >
                <CheckIcon /> Done
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex direction="column" gap="3xs">
            <ScheduleOptionItem
              option="Now"
              description="Share your post right away."
              isSelected={!value}
              isDefault={false}
              isDisabled={false}
              canSetDefault={false}
              onSelectOption={(): void => selectTime(undefined)}
            />
            <ScheduleOptionItem
              option="FixedTime"
              description="Choose a specific date and time to post."
              isSelected={!!value}
              isDefault={false}
              isDisabled={false}
              canSetDefault={false}
              onSelectOption={(_, event): void => openDatePicker(event)}
            />
          </Flex>
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
